// Knowledge Base — console-standard rebuild. Supabase-backed sections + articles
// with admin CRUD. Theme-aware (dark/light); markdown renderer themed via tokens.
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ConsoleShell, Panel, Empty, useConsoleTheme, useIsMobile } from '../components/Console';
import { supabase } from '../supabaseClient';

function inlineFormat(s, t) {
  return s
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, `<code style="background:${t.codebg};color:${t.accent};padding:1px 5px;border-radius:4px;font-family:Consolas,Monaco,monospace;font-size:.92em">$1</code>`)
    .replace(/\[(.+?)\]\((.+?)\)/g, `<a href="$2" target="_blank" rel="noopener noreferrer" style="color:${t.search};text-decoration:underline">$1</a>`);
}

function renderMd(md, t) {
  if (!md) return null;
  const lines = md.split('\n'); const out = []; let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('```')) {
      const code = []; i++;
      while (i < lines.length && !lines[i].startsWith('```')) { code.push(lines[i]); i++; }
      out.push(<pre key={'c'+i} style={{ background:t.codebg, color:t.tx, padding:'12px 14px', borderRadius:8, fontSize:'.85rem', lineHeight:1.65, overflowX:'auto', fontFamily:'Consolas,Monaco,monospace', margin:'10px 0' }}>{code.join('\n')}</pre>);
      i++; continue;
    }
    if (line.startsWith('## '))  { out.push(<h3 key={'h'+i} style={{ fontSize:'1.05rem', fontWeight:600, color:t.accent, margin:'14px 0 6px' }}>{line.slice(3)}</h3>); i++; continue; }
    if (line.startsWith('### ')) { out.push(<h4 key={'h'+i} style={{ fontSize:'0.95rem', fontWeight:600, color:t.accent, margin:'10px 0 4px' }}>{line.slice(4)}</h4>); i++; continue; }
    if (line.startsWith('|')) {
      const rows = []; while (i < lines.length && lines[i].startsWith('|')) { rows.push(lines[i]); i++; }
      out.push(
        <div key={'t'+i} style={{ overflowX:'auto', margin:'10px 0' }}>
          <table style={{ borderCollapse:'collapse', fontSize:'0.85rem', width:'100%' }}><tbody>
            {rows.filter((r, idx) => idx !== 1).map((r, j) => {
              const cells = r.split('|').slice(1, -1).map(c => c.trim()); const Tag = j === 0 ? 'th' : 'td';
              return <tr key={j}>{cells.map((c, k) => <Tag key={k} style={{ border:`.5px solid ${t.bd}`, padding:'6px 10px', textAlign:'left', background: j === 0 ? t.rowbg : 'transparent', color: j === 0 ? t.accent : t.tx, fontWeight: j === 0 ? 600 : 400 }} dangerouslySetInnerHTML={{ __html: inlineFormat(c, t) }} />)}</tr>;
            })}
          </tbody></table>
        </div>
      ); continue;
    }
    if (line.startsWith('- ')) {
      const items = []; while (i < lines.length && lines[i].startsWith('- ')) { items.push(lines[i].slice(2)); i++; }
      out.push(<ul key={'l'+i} style={{ paddingLeft:18, margin:'6px 0', fontSize:'0.95rem', color:t.tx, lineHeight:1.7 }}>{items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: inlineFormat(it, t) }} />)}</ul>);
      continue;
    }
    if (line.trim() === '') { out.push(<div key={'s'+i} style={{ height:6 }} />); i++; continue; }
    out.push(<p key={'p'+i} style={{ fontSize:'0.95rem', color:t.tx, lineHeight:1.7, margin:'4px 0' }} dangerouslySetInnerHTML={{ __html: inlineFormat(line, t) }} />);
    i++;
  }
  return out;
}

function Body() {
  const { t } = useConsoleTheme();
  const isMobile = useIsMobile();
  const [sections, setSections] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [activeArticle, setActiveArticle] = useState(null);
  const [search, setSearch] = useState('');
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ section_id:'', slug:'', title:'', tags:'', body:'' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [{ data: s, error: se }, { data: a, error: ae }] = await Promise.all([
        supabase.from('kb_sections').select('*').order('sort_order'),
        supabase.from('kb_articles').select('*').order('section_id, sort_order'),
      ]);
      if (se) throw se; if (ae) throw ae;
      setSections(s || []); setArticles(a || []);
      if (!activeSection && s && s.length) setActiveSection(s[0].id);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [activeSection]);

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { supabase.auth.getUser().then(({ data }) => setMe(data?.user || null)); }, []);

  const isAdmin = me && ['product@sanoire.com', 'emily@blynkstudio.com'].includes(me.email);

  const visibleArticles = useMemo(() => {
    let list = articles;
    if (activeSection) list = list.filter(a => a.section_id === activeSection);
    const q = search.trim().toLowerCase();
    if (q) list = articles.filter(a => a.title.toLowerCase().includes(q) || (a.body || '').toLowerCase().includes(q) || (a.tags || []).some(tg => tg.toLowerCase().includes(q)));
    return list;
  }, [articles, activeSection, search]);

  const openEdit = a => { setEditing(a); setDraft({ section_id:a.section_id, slug:a.slug, title:a.title, tags:(a.tags || []).join(', '), body:a.body || '' }); };
  const openCreate = () => { setCreating(true); setDraft({ section_id:activeSection || (sections[0]?.id || ''), slug:'', title:'', tags:'', body:'' }); };
  const saveDraft = async () => {
    setSaving(true);
    try {
      const tagsArray = draft.tags.split(',').map(x => x.trim()).filter(Boolean);
      const payload = { section_id:draft.section_id, slug:draft.slug.trim() || ('untitled-' + Date.now()), title:draft.title.trim(), tags:tagsArray, body:draft.body };
      if (editing) { const { error: e } = await supabase.from('kb_articles').update(payload).eq('id', editing.id); if (e) throw e; }
      else { const { error: e } = await supabase.from('kb_articles').insert(payload); if (e) throw e; }
      setEditing(null); setCreating(false); await load();
      const next = articles.find(a => a.section_id === payload.section_id && a.slug === payload.slug);
      if (next) setActiveArticle(next.id);
    } catch (e) { setError(e.message); }
    setSaving(false);
  };
  const deleteArticle = async a => {
    if (!window.confirm('Delete "' + a.title + '"? This cannot be undone.')) return;
    setSaving(true);
    try { const { error: e } = await supabase.from('kb_articles').delete().eq('id', a.id); if (e) throw e; setEditing(null); if (activeArticle === a.id) setActiveArticle(null); await load(); } catch (e) { setError(e.message); }
    setSaving(false);
  };

  const activeArticleObj = articles.find(a => a.id === activeArticle);
  const inp = { padding:'8px 11px', borderRadius:7, border:`1px solid ${t.bd}`, fontSize:'0.92rem', fontFamily:'inherit', background:t.rowbg, color:t.tx, outline:'none' };
  const btn = (bg, col, bd) => ({ padding:'7px 15px', borderRadius:7, background:bg, color:col, border: bd || 'none', fontSize:'0.9rem', fontWeight:500, cursor:'pointer', fontFamily:'inherit' });

  return (
    <div>
      <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:12, marginBottom:14 }}>
        <div style={{ fontSize:'0.95rem', color:t.mut }}>{articles.length} articles across {sections.length} sections</div>
        {isAdmin && <button onClick={openCreate} style={btn(t.accent, t.bg === '#f2f1ef' ? '#fff' : '#06222e')}>+ New article</button>}
      </div>

      {error && <div style={{ background:t.alert, color:t.alertc, padding:'8px 12px', borderRadius:8, marginBottom:10, fontSize:'0.9rem', border:`1px solid ${t.alertbd}` }}>{error}</div>}

      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles by title, body, or tag…"
        style={{ ...inp, width:'100%', boxSizing:'border-box', marginBottom:14, fontSize:'1rem' }} />

      {loading ? <Empty>Loading knowledge base…</Empty> : (
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '230px 290px 1fr', gap:12 }}>
          <Panel style={{ padding:'8px 4px', height:'fit-content' }}>
            {sections.map(s => (
              <div key={s.id} onClick={() => { setActiveSection(s.id); setActiveArticle(null); }}
                style={{ padding:'8px 12px', cursor:'pointer', display:'flex', alignItems:'center', gap:10, fontSize:'0.95rem', borderRadius:6,
                  borderLeft: activeSection === s.id ? `3px solid ${s.color || t.accent}` : '3px solid transparent',
                  color: activeSection === s.id ? t.tx : t.mut, fontWeight: activeSection === s.id ? 600 : 400,
                  background: activeSection === s.id ? t.rowbg : 'transparent' }}>
                <span style={{ fontSize:'1.05rem' }}>{s.icon}</span><span>{s.label}</span>
                <span style={{ marginLeft:'auto', fontSize:'0.8rem', color:t.mut }}>{articles.filter(a => a.section_id === s.id).length}</span>
              </div>
            ))}
          </Panel>

          <Panel style={{ padding:10, height:'fit-content', maxHeight:'70vh', overflowY:'auto' }}>
            {visibleArticles.length === 0 ? <Empty>No articles in this section.</Empty>
              : visibleArticles.map(a => (
                <div key={a.id} onClick={() => setActiveArticle(a.id)}
                  style={{ padding:'8px 11px', cursor:'pointer', borderRadius:8, marginBottom:2, background: activeArticle === a.id ? t.rowbg : 'transparent' }}>
                  <div style={{ fontSize:'0.95rem', fontWeight:500, color:t.tx }}>{a.title}</div>
                  {a.tags?.length > 0 && <div style={{ fontSize:'0.78rem', color:t.mut, marginTop:2 }}>{a.tags.join(' · ')}</div>}
                </div>
              ))}
          </Panel>

          <Panel style={{ padding:'20px 24px', minHeight:200 }}>
            {(editing || creating) ? (
              <div>
                <div style={{ fontSize:'1rem', fontWeight:600, marginBottom:12, color:t.tx }}>{editing ? 'Edit article' : 'New article'}</div>
                <div style={{ display:'grid', gap:10 }}>
                  <select value={draft.section_id} onChange={e => setDraft(d => ({ ...d, section_id:e.target.value }))} style={inp}>
                    {sections.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
                  </select>
                  <input value={draft.title} onChange={e => setDraft(d => ({ ...d, title:e.target.value }))} placeholder="Title" style={inp} />
                  <input value={draft.slug} onChange={e => setDraft(d => ({ ...d, slug:e.target.value }))} placeholder="url-slug (lowercase, hyphens)" style={{ ...inp, fontFamily:'Consolas,Monaco,monospace' }} />
                  <input value={draft.tags} onChange={e => setDraft(d => ({ ...d, tags:e.target.value }))} placeholder="tags, separated, by, comma" style={inp} />
                  <textarea value={draft.body} onChange={e => setDraft(d => ({ ...d, body:e.target.value }))} placeholder="Body (markdown — ## headers, **bold**, `code`, ```blocks```, | tables |)"
                    style={{ ...inp, fontFamily:'Consolas,Monaco,monospace', minHeight:280, resize:'vertical' }} />
                  <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:6 }}>
                    {editing && <button onClick={() => deleteArticle(editing)} disabled={saving} style={{ ...btn('transparent', t.danger, `1px solid ${t.bd}`), marginRight:'auto' }}>Delete</button>}
                    <button onClick={() => { setEditing(null); setCreating(false); }} style={btn('transparent', t.mut, `1px solid ${t.bd}`)}>Cancel</button>
                    <button onClick={saveDraft} disabled={saving || !draft.title} style={btn(t.accent, t.bg === '#f2f1ef' ? '#fff' : '#06222e')}>{saving ? '…' : 'Save'}</button>
                  </div>
                </div>
              </div>
            ) : activeArticleObj ? (
              <div>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10, marginBottom:4 }}>
                  <h2 style={{ fontSize:'1.3rem', fontWeight:600, color:t.tx, margin:0 }}>{activeArticleObj.title}</h2>
                  {isAdmin && <button onClick={() => openEdit(activeArticleObj)} style={{ ...btn('transparent', t.accent, `1px solid ${t.bd}`), padding:'4px 11px', fontSize:'0.85rem', flexShrink:0 }}>Edit</button>}
                </div>
                {activeArticleObj.tags?.length > 0 && <div style={{ fontSize:'0.8rem', color:t.mut, marginBottom:10 }}>{activeArticleObj.tags.join(' · ')}</div>}
                <div>{renderMd(activeArticleObj.body, t)}</div>
                <div style={{ marginTop:18, paddingTop:10, borderTop:`.5px solid ${t.bd}`, fontSize:'0.78rem', color:t.mut }}>Last updated: {activeArticleObj.updated_at ? new Date(activeArticleObj.updated_at).toLocaleString() : '—'}</div>
              </div>
            ) : <div style={{ fontSize:'0.95rem', color:t.mut, textAlign:'center', padding:'40px 20px' }}>Select an article from the list to read it.</div>}
          </Panel>
        </div>
      )}
    </div>
  );
}

export default function KnowledgeBase(props) {
  if (props && props.embed) return <Body />;
  return <ConsoleShell title="Knowledge Base"><Body /></ConsoleShell>;
}
